import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import DashboardPage from '@/pages/DashboardPage';
import SchedulePage from '@/pages/SchedulePage';
import PlaceholderPage from '@/pages/PlaceholderPage';
import TriviaPage from '@/pages/TriviaPage';
import SettingsPage from '@/pages/SettingsPage';
import { ForgotPasswordPage, ResetPasswordPage } from '@/pages/PasswordPages';
import Layout from '@/components/Layout';
import AuthCallback from '@/components/AuthCallback';
import '@/App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#000e2a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#fbdd68] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#000e2a] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#fbdd68] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronous check for Google Auth callback session_id
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="schedule/*" element={<SchedulePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="socials" element={<PlaceholderPage pageKey="socials" />} />
        <Route path="trivia" element={<TriviaPage />} />
        <Route path="bingo" element={<PlaceholderPage pageKey="bingo" />} />
        <Route path="karaoke" element={<PlaceholderPage pageKey="karaoke" />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster theme="dark" position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
